import { fonts } from '@/theme/fonts';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface PersonalInfoSectionProps {
  formData: {
    lastName: string;
    firstName: string;
    birthDate: string;
    gender: 'MALE' | 'FEMALE';
  };
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const PersonalInfoSection = ({
  formData,
  onChange,
}: PersonalInfoSectionProps) => {
  const { t } = useTranslation();

  return (
    <Section>
      <SectionTitle>{t('playerSignUp.basicInformation')}</SectionTitle>
      <GridContainer>
        <FormGroup>
          <Label $required>{t('common.lastName')}</Label>
          <Input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={onChange}
            placeholder={t('playerSignUp.firstNamePlaceholder')}
            required
          />
        </FormGroup>
        <FormGroup>
          <Label $required>{t('common.firstName')}</Label>
          <Input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={onChange}
            placeholder={t('playerSignUp.lastNamePlaceholder')}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label $required>{t('common.dateOfBirth')}</Label>
          <Input
            type="date"
            name="birthDate"
            value={formData.birthDate}
            onChange={onChange}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label $required>{t('common.gender')}</Label>
          <RadioGroup>
            <RadioLabel>
              <input
                type="radio"
                name="gender"
                value="MALE"
                checked={formData.gender === 'MALE'}
                onChange={onChange}
              />
              {t('common.male')}
            </RadioLabel>

            <RadioLabel>
              <input
                type="radio"
                name="gender"
                value="FEMALE"
                checked={formData.gender === 'FEMALE'}
                onChange={onChange}
              />
              {t('common.female')}
            </RadioLabel>
          </RadioGroup>
        </FormGroup>
      </GridContainer>
    </Section>
  );
};

export default PersonalInfoSection;

const Section = styled.section`
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e5e7eb;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: ${fonts.weight.semiBold};
  color: #374151;
  margin-bottom: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

interface LabelProps {
  $required?: boolean;
}

const Label = styled.label<LabelProps>`
  font-size: 13px;
  font-weight: ${fonts.weight.semiBold};
  color: #6b7280;

  &::after {
    content: ${(props) => (props.$required ? "' *'" : "''")};
    color: #ef4444;
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Input = styled.input`
  height: 40px;
  padding: 0 12px;
  font-size: 14px;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  outline: none;

  &:focus {
    border-color: #2563eb;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  height: 40px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
    accent-color: #2563eb;
  }
`;
